import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  ScrollText,
  BookOpen,
  Settings as SettingsIcon,
  ShieldCheck,
  ChevronLeft,
  Library,
  Tag,
  Quote,
  ShieldAlert,
  UserCog,
  UserMinus,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

const NAV_GROUPS = [
  {
    label: "Overview",
    items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    label: "Content",
    items: [
      { to: "/devotional-types", label: "Devotional Types", icon: Tag },
      { to: "/devotionals", label: "Devotionals", icon: ScrollText },
      { to: "/verse-of-the-day", label: "Verse of the Day", icon: Quote },
      { to: "/bible", label: "Bible", icon: BookOpen },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/users", label: "Users", icon: Users },
      { to: "/admin-users", label: "Admin Users", icon: ShieldCheck },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/data-deletion-requests", label: "Data Deletion Requests", icon: UserMinus },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/settings/account", label: "Account Settings", icon: UserCog },
      { to: "/system-config", label: "System Config", icon: SettingsIcon },
    ],
  },
];

export function SidebarBrand({ collapsed }) {
  return (
    <div className="flex items-center gap-2.5 px-4 h-16 border-b border-sidebar-border shrink-0">
      <div className="size-9 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shrink-0">
        <Library className="size-5 text-white" />
      </div>
      {!collapsed && (
        <div className="flex flex-col leading-tight overflow-hidden">
          <span className="text-sm font-semibold text-sidebar-foreground tracking-tight whitespace-nowrap">
            The Greater Life
          </span>
          <span className="text-[10.5px] uppercase tracking-[0.14em] text-sidebar-muted whitespace-nowrap">
            Admin Console
          </span>
        </div>
      )}
    </div>
  );
}

function NavItem({ to, label, icon: Icon, collapsed, onNavigate }) {
  return (
    <NavLink
      to={to}
      end
      onClick={onNavigate}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
          "text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-white/5",
          isActive && "bg-sidebar-accent/15 text-sidebar-foreground"
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-sidebar-accent" />
          )}
          <Icon
            className={cn(
              "size-[18px] shrink-0 transition-colors",
              isActive ? "text-sidebar-accent" : "text-sidebar-muted group-hover:text-sidebar-foreground"
            )}
          />
          {!collapsed && <span className="truncate">{label}</span>}
        </>
      )}
    </NavLink>
  );
}

export function SidebarNav({ collapsed, onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-5">
      {NAV_GROUPS.map((group) => (
        <div key={group.label}>
          {!collapsed && (
            <div className="px-3 mb-1.5 text-[10.5px] uppercase tracking-[0.14em] text-sidebar-muted font-semibold">
              {group.label}
            </div>
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavItem
                key={item.to}
                {...item}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        </div>
      ))}
      {/* Always-visible secondary link to the public Privacy Policy */}
      <div className="pt-2 mt-2 border-t border-sidebar-border">
        <NavItem
          to="/privacy"
          label="Privacy Policy"
          icon={ShieldAlert}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />
      </div>
    </nav>
  );
}

export function Sidebar({ collapsed, onToggle }) {
  return (
    <aside
      data-testid="sidebar-desktop"
      className={cn(
        "hidden md:flex flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border transition-[width] duration-200 ease-out shrink-0",
        collapsed ? "w-[68px]" : "w-64"
      )}
    >
      <SidebarBrand collapsed={collapsed} />
      <SidebarNav collapsed={collapsed} />
      <button
        type="button"
        onClick={onToggle}
        data-testid="sidebar-collapse-toggle"
        className="m-3 inline-flex items-center justify-center gap-2 rounded-lg border border-sidebar-border bg-white/[0.04] hover:bg-white/[0.08] text-sidebar-foreground/80 px-3 py-2 text-xs font-medium transition-colors"
      >
        <ChevronLeft className={cn("size-4 transition-transform", collapsed && "rotate-180")} />
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
  );
}

export function MobileDrawer({ open, onClose }) {
  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "md:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
        onClick={onClose}
        data-testid="mobile-drawer-backdrop"
      />
      {/* Drawer */}
      <aside
        data-testid="mobile-drawer"
        className={cn(
          "md:hidden fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85vw] bg-sidebar text-sidebar-foreground border-r border-sidebar-border flex flex-col transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between border-b border-sidebar-border h-16 px-4">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Library className="size-5 text-white" />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-semibold tracking-tight">The Greater Life</span>
              <span className="text-[10.5px] uppercase tracking-[0.14em] text-sidebar-muted">
                Admin Console
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="mobile-drawer-close"
            className="p-2 rounded-lg hover:bg-white/5 text-sidebar-foreground/80"
            aria-label="Close menu"
          >
            <X className="size-5" />
          </button>
        </div>
        <SidebarNav collapsed={false} onNavigate={onClose} />
      </aside>
    </>
  );
}
